import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, X, Check, Activity, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { TaskType, VitalType, HygieneType, FoodType, CarePlanTaskInsert, CarePlanTask } from '@/types/care-plan';
import { usePatientVitalRanges } from '@/hooks/usePatientVitalRanges';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { VitalRangesManager } from '@/components/patient/VitalRangesManager';
import { usePatientRole } from '@/hooks/usePatientRole';
import { useActiveShiftSync } from '@/hooks/useActiveShiftSync';
import { generateTimesFromFrequency, detectFrequency, dosesPerDay, FREQUENCY_PRESETS } from '@/lib/med-frequency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DAYS_KEYS = ['days.sun', 'days.mon', 'days.tue', 'days.wed', 'days.thu', 'days.fri', 'days.sat'];

const HYGIENE_TYPES: { value: HygieneType; label: string }[] = [
  { value: 'bath', label: 'hygiene.bath' },
  { value: 'diaper_change', label: 'hygiene.diaperChange' },
  { value: 'dressing', label: 'hygiene.dressing' },
];

const FOOD_TYPES: { value: FoodType; label: string }[] = [
  { value: 'breakfast', label: 'food.breakfast' },
  { value: 'lunch', label: 'food.lunch' },
  { value: 'snack', label: 'food.snack' },
  { value: 'dinner', label: 'food.dinner' },
];

const VITAL_UNITS: Record<string, string> = {
  blood_pressure: 'mmHg',
  glucose: 'mg/dL',
  temperature: '°C',
  oxygen_saturation: '%',
  weight: 'kg',
  heart_rate: 'bpm'
};

const getVitalTypeKey = (type: string) => {
  switch (type) {
    case 'blood_pressure': return 'bloodPressure';
    case 'glucose': return 'glucose';
    case 'temperature': return 'temperature';
    case 'oxygen_saturation': return 'oxygenSaturation';
    case 'weight': return 'weight';
    case 'heart_rate': return 'heartRate';
    default: return type;
  }
};

export default function AddTaskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId, taskId } = useParams<{ id: string; taskId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const initialType = (searchParams.get('type') as TaskType) || 'fixed_medication';

  // When editing, start with loading state to prevent flash of wrong type
  const [isLoading, setIsLoading] = useState(!!taskId);
  const [taskType, setTaskType] = useState<TaskType>(taskId ? 'fixed_medication' : initialType);

  // Common fields
  const [name, setName] = useState('');
  const [photoRequired, setPhotoRequired] = useState(false);

  // Medication fields
  const [dose, setDose] = useState('');
  const [doseUnit, setDoseUnit] = useState('mg');
  const [route, setRoute] = useState('Oral');
  const [scheduleDays, setScheduleDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [scheduleTimes, setScheduleTimes] = useState<string[]>(['08:00']);

  // Modo de definição dos horários: manual ou por frequência (a cada X horas)
  const [scheduleMode, setScheduleMode] = useState<'manual' | 'frequency'>('manual');
  const [freqStart, setFreqStart] = useState('06:00');
  const [freqInterval, setFreqInterval] = useState('8');

  // Sporadic fields
  const [minInterval, setMinInterval] = useState('');
  const [maxDoses, setMaxDoses] = useState('');

  // Vital fields
  const [vitalType, setVitalType] = useState<VitalType>('blood_pressure');
  const [rangesDialogOpen, setRangesDialogOpen] = useState(false);
  const { ranges, refetch: refetchRanges } = usePatientVitalRanges(patientId);
  const { canEditCarePlan: canEditRole, isPatientActive, isPatientSuspended, isPatientArchived } = usePatientRole(patientId);
  const canEditPatient = canEditRole && isPatientActive;
  const currentRange = ranges.find(r => r.vital_type === vitalType);

  // Hygiene fields
  const [hygieneType, setHygieneType] = useState<HygieneType>('bath');

  // Food fields
  const [foodType, setFoodType] = useState<FoodType>('breakfast');

  // Sincronização com plantões em andamento
  const { hasActiveShift, syncTask } = useActiveShiftSync(patientId);
  const [pendingSyncTaskId, setPendingSyncTaskId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Ref for tabs list to enable auto-scroll
  const tabsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (taskId) {
      fetchTask();
    }
  }, [taskId]);

  // Auto-scroll to selected tab when task type changes (especially important when editing)
  // Only scroll after loading is complete to avoid scroll during initial load
  useEffect(() => {
    if (tabsListRef.current && taskType && !isLoading) {
      const selectedTab = tabsListRef.current.querySelector(`[data-state="active"]`) as HTMLElement;
      if (selectedTab) {
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
          selectedTab.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
        }, 0);
      }
    }
  }, [taskType, isLoading]);

  const fetchTask = async () => {
    if (!taskId) return;
    try {
      const { data, error } = await supabase
        .from('care_plan_tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (error) throw error;

      const task = data as unknown as CarePlanTask;

      setTaskType(task.task_type);
      setName(task.name);
      setPhotoRequired(task.photo_required || false);

      if (task.dose) setDose(task.dose);
      if (task.dose_unit) setDoseUnit(task.dose_unit);
      if (task.administration_route) setRoute(task.administration_route);
      if (task.schedule_days) setScheduleDays(task.schedule_days);
      if (task.schedule_times) {
        setScheduleTimes(task.schedule_times);
        const detected = detectFrequency(task.schedule_times);
        if (detected && task.task_type === 'fixed_medication') {
          setScheduleMode('frequency');
          setFreqStart(detected.startTime);
          setFreqInterval(String(detected.intervalHours));
        }
      }

      if (task.is_sporadic) {
        if (task.min_interval_hours) setMinInterval(task.min_interval_hours.toString());
        if (task.max_daily_doses) setMaxDoses(task.max_daily_doses.toString());
      }

      if (task.vital_type) {
        setVitalType(task.vital_type);
      }

      if (task.hygiene_type) setHygieneType(task.hygiene_type);
      if (task.food_type) setFoodType(task.food_type);

    } catch (error) {
      console.error('Error fetching task:', error);
      toast.error(t('carePlan.errorRemovingTask')); // Reusing error message or generic error
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDay = (day: number) => {
    if (scheduleDays.includes(day)) {
      setScheduleDays(scheduleDays.filter(d => d !== day));
    } else {
      setScheduleDays([...scheduleDays, day].sort());
    }
  };

  const addTime = () => {
    setScheduleTimes([...scheduleTimes, '12:00']);
  };

  const removeTime = (index: number) => {
    setScheduleTimes(scheduleTimes.filter((_, i) => i !== index));
  };

  const updateTime = (index: number, value: string) => {
    const newTimes = [...scheduleTimes];
    newTimes[index] = value;
    setScheduleTimes(newTimes);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error(t('common.required'));
      return;
    }

    if (!patientId || !user) return;

    if (!isPatientActive) {
      toast.error(
        isPatientSuspended
          ? t('patient.suspended.writeBlocked', { defaultValue: 'Paciente suspenso. Reative a assinatura para alterar o plano de cuidados.' })
          : t('patient.archived.writeBlocked', { defaultValue: 'Paciente arquivado. Desarquive para alterar o plano de cuidados.' })
      );
      return;
    }

    setIsLoading(true);

    try {
      const taskData: CarePlanTaskInsert = {
        patient_id: patientId,
        created_by: user.id,
        task_type: taskType,
        name: name.trim(),
        photo_required: photoRequired,
      };

      if (taskType === 'fixed_medication') {
        taskData.dose = dose;
        taskData.dose_unit = doseUnit;
        taskData.administration_route = route;
        taskData.schedule_days = scheduleDays;
        taskData.schedule_times = scheduleTimes;
        taskData.is_sporadic = false;
      }

      if (taskType === 'sporadic_medication') {
        taskData.dose = dose;
        taskData.dose_unit = doseUnit;
        taskData.administration_route = route;
        taskData.is_sporadic = true;
        taskData.min_interval_hours = parseInt(minInterval) || null;
        taskData.max_daily_doses = parseInt(maxDoses) || null;
      }

      if (taskType === 'vital_sign') {
        const unit = VITAL_UNITS[vitalType];
        taskData.vital_type = vitalType;
        taskData.unit = unit;
        taskData.schedule_times = scheduleTimes;
      }

      if (taskType === 'physical_activity') {
        taskData.schedule_days = scheduleDays;
        taskData.schedule_times = scheduleTimes;
      }

      if (taskType === 'hygiene') {
        taskData.hygiene_type = hygieneType;
        taskData.schedule_times = scheduleTimes;
      }

      if (taskType === 'food') {
        taskData.food_type = foodType;
        taskData.schedule_times = scheduleTimes;
      }

      let savedTaskId: string | null = null;

      if (taskId) {
        const { error } = await supabase
          .from('care_plan_tasks')
          .update(taskData as any)
          .eq('id', taskId);

        if (error) throw error;
        savedTaskId = taskId;
        toast.success(t('common.success'));
      } else {
        const { data: inserted, error } = await supabase
          .from('care_plan_tasks')
          .insert(taskData as any)
          .select('id')
          .single();

        if (error) throw error;
        // `care_plan_created` is emitted server-side (trigger on care_plan_tasks).
        savedTaskId = (inserted as any)?.id ?? null;
        toast.success(t('common.success'));
      }

      // Se há plantão em andamento, perguntar se deve ajustar as atividades do plantão
      const isSchedulable = taskType !== 'sporadic_medication';
      if (savedTaskId && hasActiveShift && isSchedulable) {
        setPendingSyncTaskId(savedTaskId);
        setIsLoading(false);
        return;
      }

      navigate(`/app/patients/${patientId}/care-plan`);
    } catch (error: any) {
      console.error('Error saving task:', error);
      const inactive = (error?.message ?? '').toLowerCase().includes('row-level security') || error?.code === '42501';
      toast.error(inactive
        ? 'Esta ação não é permitida para pacientes suspensos ou arquivados.'
        : (error.message || t('common.error')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSync = async () => {
    if (!pendingSyncTaskId) return;
    setIsSyncing(true);
    try {
      const result = await syncTask(pendingSyncTaskId);
      toast.success(
        result && (result.added || result.removed || result.updated)
          ? t('carePlan.activeShiftSyncDoneDetail', {
              added: result.added,
              removed: result.removed,
              updated: result.updated,
            })
          : t('carePlan.activeShiftSyncDone'),
      );
    } catch (error) {
      console.error('Error syncing active shifts:', error);
      toast.error(t('carePlan.activeShiftSyncError'));
    } finally {
      setIsSyncing(false);
      setPendingSyncTaskId(null);
      navigate(`/app/patients/${patientId}/care-plan`);
    }
  };

  const handleSkipSync = () => {
    setPendingSyncTaskId(null);
    navigate(`/app/patients/${patientId}/care-plan`);
  };

  const getTabColor = (type: TaskType) => {
    const activeBase = 'data-[state=active]:ring-2 data-[state=active]:ring-offset-1 data-[state=active]:font-semibold';
    switch (type) {
      case 'fixed_medication': return `${activeBase} data-[state=active]:bg-primary data-[state=active]:text-primary-foreground`;
      case 'sporadic_medication': return `${activeBase} data-[state=active]:bg-sporadic data-[state=active]:text-sporadic-foreground`;
      case 'vital_sign': return `${activeBase} data-[state=active]:bg-info data-[state=active]:text-info-foreground`;
      case 'hygiene': return `${activeBase} data-[state=active]:bg-primary data-[state=active]:text-primary-foreground`;
      case 'food': return `${activeBase} data-[state=active]:bg-warning data-[state=active]:text-warning-foreground`;
      case 'physical_activity': return `${activeBase} data-[state=active]:bg-success data-[state=active]:text-success-foreground`;
      default: return `${activeBase} data-[state=active]:bg-muted-foreground data-[state=active]:text-foreground`;
    }
  };

  return (
    <AppLayout
      title={taskId ? t('carePlan.editTask') : t('carePlan.addTask')}
      showBack
      onBack={() => navigate(`/app/patients/${patientId}/care-plan`)}
      showFAB={false}
    >
      <div className="p-4">
        {(isPatientSuspended || isPatientArchived) && (
          <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            {isPatientSuspended
              ? t('patient.suspended.writeBlocked', { defaultValue: 'Paciente suspenso. Reative a assinatura para alterar o plano de cuidados.' })
              : t('patient.archived.writeBlocked', { defaultValue: 'Paciente arquivado. Desarquive para alterar o plano de cuidados.' })}
          </div>
        )}

        {/* Task Type Tabs - Hide until loaded when editing to prevent flash */}
        {taskId && isLoading ? (
          <div className="mb-6 h-10 bg-muted rounded-md animate-pulse" />
        ) : (
          <Tabs value={taskType} onValueChange={(v) => setTaskType(v as TaskType)} className="mb-6">
            <TabsList ref={tabsListRef} className="!flex justify-start overflow-x-auto h-auto p-1 w-full gap-1">
              <TabsTrigger value="fixed_medication" className={cn("text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap", getTabColor('fixed_medication'))}>
                {t('carePlan.fixedMeds')}
              </TabsTrigger>
              <TabsTrigger value="sporadic_medication" className={cn("text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap", getTabColor('sporadic_medication'))}>
                {t('carePlan.sporadicMeds')}
              </TabsTrigger>
              <TabsTrigger value="vital_sign" className={cn("text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap", getTabColor('vital_sign'))}>
                {t('carePlan.vitals')}
              </TabsTrigger>
              <TabsTrigger value="hygiene" className={cn("text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap", getTabColor('hygiene'))}>
                {t('carePlan.hygiene')}
              </TabsTrigger>
              <TabsTrigger value="food" className={cn("text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap", getTabColor('food'))}>
                {t('carePlan.food')}
              </TabsTrigger>
              <TabsTrigger value="physical_activity" className={cn("text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap", getTabColor('physical_activity'))}>
                {t('carePlan.physicalActivity')}
              </TabsTrigger>
              <TabsTrigger value="other" className="text-xs py-2 px-3 flex-shrink-0 whitespace-nowrap">
                {t('carePlan.otherTasks')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <form onSubmit={handleSubmit}>
          {/* Common Fields */}
          <Card className="mb-4">
            <CardContent className="pt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('carePlan.taskName')} *</Label>
                <Input
                  id="name"
                  placeholder={taskType.includes('medication') ? 'Ex: Losartana' : t('carePlan.taskName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="photo-required">{t('carePlan.photoRequired')}</Label>
                <Switch
                  id="photo-required"
                  checked={photoRequired}
                  onCheckedChange={setPhotoRequired}
                />
              </div>
            </CardContent>
          </Card>

          {/* Medication Fields */}
          {(taskType === 'fixed_medication' || taskType === 'sporadic_medication') && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('carePlan.medication.dosage')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="dose">Dose</Label>
                    <Input
                      id="dose"
                      placeholder="Ex: 50"
                      value={dose}
                      onChange={(e) => setDose(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dose-unit">Unidade</Label>
                    <Select value={doseUnit} onValueChange={setDoseUnit}>
                      <SelectTrigger id="dose-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mg">mg</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                        <SelectItem value="gotas">gotas</SelectItem>
                        <SelectItem value="comprimido">comprimido</SelectItem>
                        <SelectItem value="UI">UI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="route">{t('carePlan.medication.route')}</Label>
                  <Select value={route} onValueChange={setRoute}>
                    <SelectTrigger id="route">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Oral">Oral</SelectItem>
                      <SelectItem value="Sublingual">Sublingual</SelectItem>
                      <SelectItem value="Tópico">Tópico</SelectItem>
                      <SelectItem value="Inalatório">Inalatório</SelectItem>
                      <SelectItem value="Subcutâneo">Subcutâneo</SelectItem>
                      <SelectItem value="Intramuscular">Intramuscular</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fixed Medication Schedule */}
          {taskType === 'fixed_medication' && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('carePlan.scheduleProgram')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('carePlan.scheduleDays')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_KEYS.map((dayKey, index) => {
                      const isSelected = scheduleDays.includes(index);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => toggleDay(index)}
                          className={cn(
                            "relative flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-all border-2",
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary shadow-sm"
                              : "bg-background text-muted-foreground border-border hover:border-muted-foreground/50"
                          )}
                        >
                          <Check className={cn("w-3 h-3 absolute top-0.5 right-0.5 transition-opacity", isSelected ? "opacity-100" : "opacity-0")} />
                          {t(dayKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>{t('carePlan.scheduleTime')}</Label>

                  {/* Modo: horários manuais ou por frequência */}
                  <div className="grid grid-cols-2 gap-2">
                    {(['manual', 'frequency'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          setScheduleMode(mode);
                          if (mode === 'frequency') {
                            const generated = generateTimesFromFrequency(freqStart, Number(freqInterval));
                            if (generated.length > 0) setScheduleTimes(generated);
                          }
                        }}
                        className={cn(
                          'rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all',
                          scheduleMode === mode
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-muted-foreground/50',
                        )}
                      >
                        {mode === 'manual' ? t('carePlan.scheduleModeManual') : t('carePlan.scheduleModeFrequency')}
                      </button>
                    ))}
                  </div>

                  {scheduleMode === 'frequency' && (
                    <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="freq-start" className="text-xs">{t('carePlan.frequencyStart')}</Label>
                          <Input
                            id="freq-start"
                            type="time"
                            value={freqStart}
                            onChange={(e) => {
                              setFreqStart(e.target.value);
                              const generated = generateTimesFromFrequency(e.target.value, Number(freqInterval));
                              if (generated.length > 0) setScheduleTimes(generated);
                            }}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="freq-interval" className="text-xs">{t('carePlan.frequencyInterval')}</Label>
                          <Select
                            value={FREQUENCY_PRESETS.map(String).includes(freqInterval) ? freqInterval : 'custom'}
                            onValueChange={(value) => {
                              if (value === 'custom') return;
                              setFreqInterval(value);
                              const generated = generateTimesFromFrequency(freqStart, Number(value));
                              if (generated.length > 0) setScheduleTimes(generated);
                            }}
                          >
                            <SelectTrigger id="freq-interval">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FREQUENCY_PRESETS.map((hours) => (
                                <SelectItem key={hours} value={String(hours)}>
                                  {t('carePlan.everyXHours', { hours })}
                                </SelectItem>
                              ))}
                              {!FREQUENCY_PRESETS.map(String).includes(freqInterval) && (
                                <SelectItem value="custom">
                                  {t('carePlan.everyXHours', { hours: freqInterval })}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('carePlan.frequencySummary', {
                          hours: freqInterval,
                          count: dosesPerDay(Number(freqInterval)),
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">{t('carePlan.frequencyEditableHint')}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    {scheduleTimes.map((time, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={time}
                          onChange={(e) => updateTime(index, e.target.value)}
                          className="flex-1"
                        />
                        {scheduleTimes.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTime(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="ghost" size="sm" onClick={addTime}>
                      <Plus className="w-4 h-4 mr-1" />
                      {t('common.add')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sporadic Medication Fields */}
          {taskType === 'sporadic_medication' && (
            <Card className="mb-4 border-sporadic/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-sporadic">{t('carePlan.useRules')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="min-interval">{t('carePlan.minInterval')} ({t('carePlan.medication.unit')})</Label>
                  <Input
                    id="min-interval"
                    type="number"
                    placeholder="Ex: 4"
                    value={minInterval}
                    onChange={(e) => setMinInterval(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-doses">{t('carePlan.maxDailyDose')}</Label>
                  <Input
                    id="max-doses"
                    type="number"
                    placeholder="Ex: 3"
                    value={maxDoses}
                    onChange={(e) => setMaxDoses(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vital Sign Fields */}
          {taskType === 'vital_sign' && (
            <Card className="mb-4 border-success/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-success">{t('carePlan.config')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('carePlan.vitals')}</Label>
                  <Select value={vitalType} onValueChange={(v) => setVitalType(v as VitalType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(VITAL_UNITS).map((key) => {
                        return (
                          <SelectItem key={key} value={key}>
                            {t(`vitals.${getVitalTypeKey(key)}`)} ({VITAL_UNITS[key]})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Patient-level vital ranges info (centralized) */}
                <div className="rounded-lg border border-info/30 bg-info/5 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Activity className="w-4 h-4 text-info mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {t('carePlan.vitalRangesInfoTitle', { defaultValue: 'Limites do paciente' })}
                      </p>
                      {currentRange ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          {vitalType === 'blood_pressure' ? (
                            <>
                              {t('vitals.systolic', { defaultValue: 'Sistólica' })}: {currentRange.min_value ?? '?'}–{currentRange.max_value ?? '?'}
                              {currentRange.min_value_secondary != null && currentRange.max_value_secondary != null && (
                                <> · {t('vitals.diastolic', { defaultValue: 'Diastólica' })}: {currentRange.min_value_secondary}–{currentRange.max_value_secondary}</>
                              )}
                              {' '}{currentRange.unit || 'mmHg'}
                            </>
                          ) : (
                            <>{currentRange.min_value ?? '?'} – {currentRange.max_value ?? '?'} {currentRange.unit || VITAL_UNITS[vitalType]}</>
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('carePlan.vitalRangesNotConfigured', { defaultValue: 'Nenhum limite configurado para este sinal vital. Os alertas só serão gerados após configurar.' })}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {t('carePlan.vitalRangesInfoHint', { defaultValue: 'Os limites são definidos por paciente e valem para todas as tarefas e leituras desse sinal vital.' })}
                      </p>
                    </div>
                  </div>
                  {canEditPatient && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setRangesDialogOpen(true)}
                    >
                      <Settings2 className="w-4 h-4 mr-2" />
                      {t('carePlan.configurePatientLimits', { defaultValue: 'Configurar limites do paciente' })}
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t('carePlan.scheduleTime')}</Label>
                  <Input
                    type="time"
                    value={scheduleTimes[0]}
                    onChange={(e) => setScheduleTimes([e.target.value])}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Physical Activity Fields */}
          {taskType === 'physical_activity' && (
            <Card className="mb-4 border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-primary">{t('carePlan.scheduleProgram')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('carePlan.scheduleDays')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_KEYS.map((dayKey, index) => {
                      const isSelected = scheduleDays.includes(index);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => toggleDay(index)}
                          className={cn(
                            "relative flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-all border-2",
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary shadow-sm"
                              : "bg-background text-muted-foreground border-border hover:border-muted-foreground/50"
                          )}
                        >
                          <Check className={cn("w-3 h-3 absolute top-0.5 right-0.5 transition-opacity", isSelected ? "opacity-100" : "opacity-0")} />
                          {t(dayKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t('carePlan.scheduleTime')}</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={addTime}>
                      <Plus className="w-4 h-4 mr-1" />
                      {t('common.add')}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {scheduleTimes.map((time, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={time}
                          onChange={(e) => updateTime(index, e.target.value)}
                          className="flex-1"
                        />
                        {scheduleTimes.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTime(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hygiene Fields */}
          {taskType === 'hygiene' && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('carePlan.hygiene')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select value={hygieneType} onValueChange={(v) => setHygieneType(v as HygieneType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HYGIENE_TYPES.map((ht) => (
                      <SelectItem key={ht.value} value={ht.value}>
                        {t(`hygiene.${ht.value.replace(/_([a-z])/g, (g) => g[1].toUpperCase())}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="space-y-2">
                  <Label>{t('carePlan.preferredTime')}</Label>
                  <Input
                    type="time"
                    value={scheduleTimes[0]}
                    onChange={(e) => setScheduleTimes([e.target.value])}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Food Fields */}
          {taskType === 'food' && (
            <Card className="mb-4 border-warning/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-warning">{t('carePlan.food')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select value={foodType} onValueChange={(v) => setFoodType(v as FoodType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOOD_TYPES.map((ft) => (
                      <SelectItem key={ft.value} value={ft.value}>
                        {t(`food.${ft.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="space-y-2">
                  <Label>{t('carePlan.scheduleTime')}</Label>
                  <Input
                    type="time"
                    value={scheduleTimes[0]}
                    onChange={(e) => setScheduleTimes([e.target.value])}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? t('common.loading') : t('common.save')}
          </Button>
        </form>
      </div>

      {patientId && (
        <Dialog
          open={rangesDialogOpen}
          onOpenChange={(open) => {
            setRangesDialogOpen(open);
            if (!open) refetchRanges();
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {t('vitalRanges.title', { defaultValue: 'Limites Vitais' })}
              </DialogTitle>
            </DialogHeader>
            <VitalRangesManager patientId={patientId} canEdit={canEditPatient} />
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={!!pendingSyncTaskId} onOpenChange={(open) => { if (!open && !isSyncing) handleSkipSync(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('carePlan.activeShiftSyncTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('carePlan.activeShiftSyncAddDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSyncing} onClick={handleSkipSync}>
              {t('carePlan.activeShiftSyncSkip')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isSyncing}
              onClick={(e) => { e.preventDefault(); handleConfirmSync(); }}
            >
              {t('carePlan.activeShiftSyncApply')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
